import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Inbox } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import { getAppHomePath } from '../../constants/navigation';
import { institutionService, meService, processService } from '../../services/api';
import { useTranslation } from 'react-i18next';
import SlaBadge, { tramitacaoDueDate } from '../../components/Common/SlaBadge';

const SIT_MOVE = { 1: 'sitPending', 2: 'sitReceived', 3: 'sitForwarded', 4: 'sitReturned' };
const TABS = [
  { id: 'minha', labelKey: 'tabMine' },
  { id: 'setor', labelKey: 'tabSector' },
  { id: 'orgao', labelKey: 'tabAgency' },
  { id: 'enviar', labelKey: 'tabSend' },
  { id: 'historico', labelKey: 'tabHistory' },
];

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

function InboxTable({ items, orgaos, isLoading, error, emptyLabel, t }) {
  return (
    <div className="data-list-card overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Processo</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">{t('destination')}</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Situação</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">SLA</th>
            <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500"> </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {isLoading ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                Carregando...
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-red-600">
                {t('common:loadError')}
              </td>
            </tr>
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                <Inbox className="mx-auto mb-2 h-6 w-6" />
                {emptyLabel}
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {item.processo_numero || item.processo_id}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {orgaos.find((o) => o.id === item.orgao_destino_id)?.name || item.orgao_destino_id}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {t(SIT_MOVE[item.situacao] || 'sitPending')}
                </td>
                <td className="px-6 py-4 text-sm">
                  <SlaBadge prazo={tramitacaoDueDate(item)} />
                </td>
                <td className="px-6 py-4 text-right text-sm">
                  <Link
                    to={`/process/cases/${item.processo_id}`}
                    className="text-primary-600 hover:text-primary-800"
                  >
                    {t('openDetail')}
                  </Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

const ProcessoRouting = () => {
  const { t } = useTranslation('process');
  const [tab, setTab] = useState('minha');
  const [orgaoId, setOrgaoId] = useState('');

  const { data: orgaosRaw } = useQuery(['orgaos'], () =>
    institutionService.orgaoCrud.getList({ limit: 500 })
  );
  const { data: meServidor } = useQuery(['me-servidor'], () =>
    meService.getServidor().then((r) => r.data)
  );
  const orgaos = unwrap(orgaosRaw);
  const myServidorId = meServidor?.servidor_id || null;
  const mySetorId = meServidor?.setor_id || null;
  const orgaosRoteamento = useMemo(
    () => orgaos.filter((o) => o.receive_routing !== false),
    [orgaos]
  );

  const inboxExtra = useMemo(() => {
    const extra = {};
    if (tab === 'historico') extra.situacoes = '3,4';
    else extra.situacoes = '1,2';
    if (tab === 'minha') {
      extra.escopo = 'minha';
      if (myServidorId) extra.servidor_id = myServidorId;
    }
    if (tab === 'setor') {
      extra.escopo = 'setor';
      if (mySetorId) extra.setor_id = mySetorId;
    }
    return extra;
  }, [tab, myServidorId, mySetorId]);

  const inboxEnabled =
    tab !== 'enviar' && !(tab === 'minha' && !myServidorId) && !(tab === 'setor' && !mySetorId);
  const { data, isLoading, error } = useQuery(
    ['process-routing', tab, orgaoId, myServidorId, mySetorId],
    () =>
      processService
        .getInbox(orgaoId || undefined, inboxExtra)
        .then((r) => r.data),
    { enabled: inboxEnabled }
  );
  const items =
    (tab === 'minha' && !myServidorId) || (tab === 'setor' && !mySetorId) ? [] : unwrap(data);

  const { data: processosRaw, isLoading: loadingSend } = useQuery(
    ['processos-routing-send'],
    () => processService.getProcessos({ limit: 200 }).then((r) => r.data),
    { enabled: tab === 'enviar' }
  );
  const processos = unwrap(processosRaw);

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={t('routingTitle')}
          description={t('routingSubtitle')}
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: t('title'), to: '/process/cases' },
            { label: t('routingTitle') },
          ]}
        />

        <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('routingTitle')}>
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                tab === item.id
                  ? 'bg-primary-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setTab(item.id)}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>

        {tab !== 'enviar' ? (
          <div className="form-group max-w-md">
            <label htmlFor="routing-orgao" className="form-label">
              Órgão
            </label>
            <select
              id="routing-orgao"
              className="form-select"
              value={orgaoId}
              onChange={(e) => setOrgaoId(e.target.value)}
            >
              <option value="">Todos</option>
              {orgaosRoteamento.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || o.id}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {tab === 'minha' && !myServidorId ? (
          <p className="text-sm text-amber-700">{t('noLinkedServidor')}</p>
        ) : null}
        {tab === 'setor' && !mySetorId ? (
          <p className="text-sm text-amber-700">{t('noLinkedSetor')}</p>
        ) : null}

        {tab === 'enviar' ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">{t('sendHint')}</p>
            <div className="data-list-card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                      Processo
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500"> </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loadingSend ? (
                    <tr>
                      <td colSpan={2} className="px-6 py-8 text-center text-gray-500">
                        Carregando...
                      </td>
                    </tr>
                  ) : processos.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-6 py-8 text-center text-gray-500">
                        {t('emptyInbox')}
                      </td>
                    </tr>
                  ) : (
                    processos.map((proc) => (
                      <tr key={proc.id}>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          {proc.numero || proc.id}
                        </td>
                        <td className="px-6 py-4 text-right text-sm">
                          <Link
                            to={`/process/cases/${proc.id}?tramitar=1`}
                            className="text-primary-600 hover:text-primary-800"
                          >
                            {t('openToForward')}
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <InboxTable
            items={items}
            orgaos={orgaos}
            isLoading={isLoading}
            error={error}
            emptyLabel={tab === 'historico' ? t('emptyHistory') : t('emptyInbox')}
            t={t}
          />
        )}
      </div>
    </FormPageShell>
  );
};

export default ProcessoRouting;
